"use client";

import {
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useSidebar,
} from "@devhop/ui";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import {
  CreditCard,
  LayoutDashboard,
  MenuIcon,
  Package,
  Wrench,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  getMobileMoreNavLinks,
  getVisibleMerchantNavGroups,
  isMerchantNavLinkActive,
} from "@/app/layout/merchant-nav-filter";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";

type TabDef = {
  key: string;
  label: string;
  url: string;
  icon: typeof LayoutDashboard;
};

const BASE_TABS: TabDef[] = [
  {
    key: "dashboard",
    label: "ແຜງ",
    url: "/app/shops/$shopId",
    icon: LayoutDashboard,
  },
  {
    key: "pos",
    label: "ຂາຍ",
    url: "/app/shops/$shopId/pos",
    icon: CreditCard,
  },
  {
    key: "catalog",
    label: "ສິນຄ້າ",
    url: "/app/shops/$shopId/catalog",
    icon: Package,
  },
];

export function MerchantMobileBottomNav() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const href = useLocation({ select: (l) => l.href });
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const { setOpenMobile } = useSidebar();
  const [moreOpen, setMoreOpen] = useState(false);

  const showWorkshop =
    hasShopFeature(currentShop?.featureFlags, "workshop") &&
    canUseMerchantPermission(
      currentPermissions,
      MerchantPermissions.workshop.read,
      currentShop?.verifiedLevel ?? null,
    );

  const tabs = useMemo(() => {
    if (!showWorkshop) return BASE_TABS;
    return [
      ...BASE_TABS.slice(0, 2),
      {
        key: "workshop",
        label: "ສ້ອມ",
        url: "/app/shops/$shopId/workshop",
        icon: Wrench,
      },
      ...BASE_TABS.slice(2),
    ];
  }, [showWorkshop]);

  const moreLinks = useMemo(() => {
    if (!shopId) return [];
    const groups = getVisibleMerchantNavGroups(
      currentPermissions,
      currentShop?.featureFlags,
    );
    return getMobileMoreNavLinks(groups);
  }, [shopId, currentPermissions, currentShop?.featureFlags]);

  if (!shopId) return null;

  const isMoreActive = moreLinks.some((l) =>
    isMerchantNavLinkActive(href, l.url, shopId),
  );

  const colClass =
    tabs.length >= 4 ? "grid-cols-5" : "grid-cols-4";

  return (
    <>
      <nav
        aria-label="ເມນູລຸ່ມ"
        className="fixed inset-x-0 bottom-0 z-40 border-border border-t bg-background/95 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-4px_24px_-8px_rgba(0,0,0,0.12)] backdrop-blur-md md:hidden"
      >
        <ul className={cn("mx-auto grid max-w-lg", colClass)}>
          {tabs.map((tab) => {
            const active =
              tab.key === "pos"
                ? isMerchantNavLinkActive(href, tab.url, shopId) &&
                  !href.includes("/pos/sales")
                : isMerchantNavLinkActive(href, tab.url, shopId);
            const Icon = tab.icon;
            return (
              <li key={tab.key}>
                <Link
                  to={tab.url}
                  params={{ shopId }}
                  className={cn(
                    "flex min-h-13 flex-col items-center justify-center gap-0.5 px-0.5 py-2 font-medium text-[10px] touch-manipulation",
                    active
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon
                    className={cn("size-5", active && "text-primary")}
                    aria-hidden
                  />
                  {tab.label}
                </Link>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={cn(
                "flex min-h-13 w-full flex-col items-center justify-center gap-0.5 px-0.5 py-2 font-medium text-[10px] touch-manipulation",
                isMoreActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <MenuIcon
                className={cn("size-5", isMoreActive && "text-primary")}
                aria-hidden
              />
              ອື່ນໆ
            </button>
          </li>
        </ul>
      </nav>

      <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogContent className="max-h-[min(80dvh,32rem)] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>ເມນູອື່ນໆ</DialogTitle>
          </DialogHeader>
          <ul className="grid gap-1">
            {moreLinks.map((link) => {
              const active = isMerchantNavLinkActive(href, link.url, shopId);
              return (
                <li key={link.url}>
                  <Link
                    to={link.url}
                    params={{ shopId }}
                    onClick={() => setMoreOpen(false)}
                    className={cn(
                      "flex min-h-11 items-center rounded-lg px-3 py-2.5 font-medium text-sm touch-manipulation",
                      active
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted",
                    )}
                  >
                    {link.title}
                  </Link>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className="mt-2 flex min-h-11 w-full items-center justify-center rounded-lg border border-border font-medium text-sm touch-manipulation hover:bg-muted"
            onClick={() => {
              setMoreOpen(false);
              setOpenMobile(true);
            }}
          >
            ເມນູຂ້າງ (sidebar)
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
